import React from 'react'
import { useLocation } from 'react-router-dom'

import { StatusTag } from '@components/common/StatusTag'
import { List, useMediaQueryContext } from '@fourthwall/components'
import { getProductLink } from '@modules/Dashboard/Collection/utils'
import type { FromLocationState } from '@types'

import type { Offers } from '../../types'
import { SelectedList } from '../SelectedList'
import { SelectedProductsListItem } from '../SelectedProductsListItem'

type SelectedProductsProps = {
  collectionName: string
  offers: Offers
  selectedProductsIds: string[]
  disableSorting?: boolean
  onRemove: (index: number) => void
  onSortChange: (newIndex: number, oldIndex: number) => void
  onPositionChange: (index: number) => void
}

export const SelectedProducts = ({
  offers,
  collectionName,
  selectedProductsIds,
  disableSorting = false,
  onRemove,
  onSortChange,
  onPositionChange,
}: SelectedProductsProps) => {
  const media = useMediaQueryContext()
  const location = useLocation()

  const locationState = {
    from: { label: collectionName, location },
  } satisfies FromLocationState

  const getProduct = (productId: string) => (offers ? offers[productId] : null)

  if (!media.tablet) {
    return (
      <List
        data={selectedProductsIds}
        topBorder={false}
        getItemLink={(productId: string) => getProductLink(productId)}
        locationState={locationState}
        renderItem={(productId: string, index: number) => {
          const product = getProduct(productId)

          if (!product) return null

          return (
            <SelectedProductsListItem
              product={product}
              disabled={disableSorting}
              onRemove={() => onRemove(index)}
              onPositionChange={() => onPositionChange(index)}
            />
          )
        }}
      />
    )
  }

  const renderSelectedProducts = () => {
    return selectedProductsIds.map((productId) => {
      const product = getProduct(productId)

      if (!product) return null

      return (
        <SelectedList.Item
          id={product.id}
          name={product.name}
          link={getProductLink(product.id)}
          photoUrl={product.primaryImage?.thumbnail.url}
          rightContent={
            <div style={{ display: 'flex' }}>
              <StatusTag status={product.state.status} isSoldOut={!product.state.available} />
            </div>
          }
        />
      )
    })
  }

  return (
    <SelectedList
      disableSorting={disableSorting}
      onRemove={onRemove}
      onSortChange={onSortChange}
      onPositionChange={onPositionChange}
    >
      {renderSelectedProducts()}
    </SelectedList>
  )
}
